import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AiSuggestInvalidError,
  AiSuggestResponse,
  suggestSearchQuery,
} from "../api/aiSearch";
import { ApiError, extractErrorMessage } from "../api/client";

// "Ask AI" dialog for the Lucene-search step. Two modes:
//   - current is empty → "build a search from scratch"
//   - current is set   → "refine the current search"
// Result preview shows the suggested query + the model's explanation;
// Apply replaces whatever's in the editor (the user confirmed this
// behavior in the wizard discussion 2026-05-29).

interface Props {
  open: boolean;
  current: string;
  onClose: () => void;
  onApply: (query: string) => void;
}

export function AiSearchDialog({ open, current, onClose, onApply }: Props) {
  const [intent, setIntent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<AiSuggestInvalidError | null>(null);
  const [suggestion, setSuggestion] = useState<AiSuggestResponse | null>(null);

  // Reset transient state when the dialog opens. Otherwise a prior
  // suggestion would still be visible the next time the user opens it,
  // which is confusing — they expect a fresh prompt each invocation.
  useEffect(() => {
    if (open) {
      setIntent("");
      setLoading(false);
      setError(null);
      setInvalid(null);
      setSuggestion(null);
    }
  }, [open]);

  const refining = current.trim().length > 0;

  const handleSuggest = async () => {
    if (!intent.trim()) return;
    setLoading(true);
    setError(null);
    setInvalid(null);
    setSuggestion(null);
    try {
      const r = await suggestSearchQuery({
        intent: intent.trim(),
        current: refining ? current : undefined,
      });
      setSuggestion(r);
    } catch (err) {
      if (err instanceof AiSuggestInvalidError) {
        setInvalid(err);
      } else {
        setError(
          extractErrorMessage(
            err,
            err instanceof ApiError ? `AI request returned ${err.status}` : undefined,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!suggestion) return;
    onApply(suggestion.query);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {refining ? "Refine search with AI" : "Build search with AI"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Describe what you want to find in the transcripts. The assistant
          knows transcripts are noisy (STT errors, misspellings), knows
          the operators paim supports, and won't try to filter on station
          or date — those are handled by the metadata filter step.
        </DialogContentText>

        {refining && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Current query
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 1.5,
                m: 0,
                mt: 0.5,
                bgcolor: "action.hover",
                borderRadius: 1,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: "0.85rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {current}
            </Box>
          </Box>
        )}

        <TextField
          autoFocus
          multiline
          fullWidth
          rows={3}
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder={
            refining
              ? "e.g. broaden to catch alternate spellings; require any mention of 'oil refinery'"
              : "e.g. anything about charlie kirk OR turning point, must mention 'campus'"
          }
          disabled={loading}
        />

        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Thinking…
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {invalid && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {invalid.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Attempted query:
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 1,
                m: 0.5,
                bgcolor: "background.paper",
                borderRadius: 1,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: "0.8rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {invalid.query}
            </Box>
            {invalid.explanation && (
              <Typography variant="caption" color="text.secondary">
                {invalid.explanation}
              </Typography>
            )}
          </Alert>
        )}

        {suggestion && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Suggested query
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 1.5,
                m: 0,
                mt: 0.5,
                bgcolor: "action.hover",
                borderRadius: 1,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: "0.9rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {suggestion.query}
            </Box>
            {suggestion.explanation && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                {suggestion.explanation}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {suggestion ? (
          <>
            <Button onClick={handleSuggest} disabled={loading || !intent.trim()}>
              Try again
            </Button>
            <Button onClick={handleApply} variant="contained">
              Use this query
            </Button>
          </>
        ) : (
          <Button
            onClick={handleSuggest}
            variant="contained"
            disabled={loading || !intent.trim()}
          >
            Suggest
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
